import { useNavigate, useParams } from "react-router";
import type { GoodsImages } from "@laftel-ip-originals/notion/contracts";
import { resolveGoodsImage } from "@laftel-ip-originals/notion/locale";
import { useI18n } from "~/i18n";
import { resolveIpAssets } from "~/theme/resolve-theme";
import { getOriginalBySlug } from "~/data/ip";
import { SECTION_TOP_DIVIDER } from "~/components/sections/section-top-divider";
import { SectionHeading } from "~/components/sections/section-heading";
import {
  GoodsTitle,
  formatGoodsTitle,
  type GoodsTitleParts,
} from "~/components/sections/goods-title";

interface MerchandiseProps {
  hasGoods: boolean;
  coverImage?: GoodsImages;
  isLoading?: boolean;
}

export function Merchandise({ hasGoods, coverImage, isLoading = false }: MerchandiseProps) {
  const navigate = useNavigate();
  const { slug = "" } = useParams<{ slug: string }>();
  const assets = resolveIpAssets(slug);
  const { locale, t } = useI18n();
  const original = getOriginalBySlug(slug);

  const coverUrl = resolveGoodsImage(coverImage, locale);

  if (!hasGoods && !isLoading) return null;
  if (!coverUrl && !isLoading) return null;

  const titleParts: GoodsTitleParts | null = original
    ? {
        workName: original.goodsTitle?.[locale] ?? original.title[locale],
        subtitle: t.md.subtitle,
        locale,
      }
    : null;
  const titleText = titleParts ? formatGoodsTitle(titleParts) : "";

  return (
    <section
      id="goods"
      className={`merchandise relative z-[2] bg-white pt-[60px] pb-[100px] ${SECTION_TOP_DIVIDER}`}
    >
      <div className="max-w-full mx-auto px-[clamp(24px,5vw,120px)]">
        <SectionHeading imgSrc={assets.sectionTitles.goods} alt="Goods" />

        <div className="merchandise__banners mx-auto max-w-[900px] flex flex-col gap-5">
          {isLoading ? (
            <div className="overflow-hidden rounded-[16px]">
              <div className="skeleton aspect-[16/9] w-full" />
            </div>
          ) : (
            <button
              onClick={() => navigate(`/${slug}/goods`, { state: { from: "goods" } })}
              className="merchandise__banner group relative block w-full overflow-hidden rounded-[16px] cursor-pointer"
            >
              <div className="relative w-full aspect-[16/9] bg-[var(--ip-surface)]">
                <img
                  src={coverUrl ?? ""}
                  alt={titleText}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03] group-active:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/40" />
                <div className="absolute top-6 left-7 flex flex-col gap-0.5">
                  <span className="text-[22px] font-bold tracking-[0.04em] text-white opacity-0 -translate-x-4 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0">
                    {titleParts && <GoodsTitle parts={titleParts} />}
                  </span>
                  <span className="text-[14px] tracking-[0.06em] text-white text-left opacity-0 -translate-x-4 transition-all duration-300 delay-100 group-hover:opacity-100 group-hover:translate-x-0">
                    {t.md.viewDetail} →
                  </span>
                </div>
              </div>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
